import { Component, ojs, app, state, v } from "modular-openscriptjs";
import { tool } from "../../fotastart";
import { authenticated, cleanDispose, eParse, isMobile } from "../../helpers";
import { Preferences } from "@capacitor/preferences";
import LoginOptions from "../Blocks/LoginOptions";

const h = app("h");

ojs(LoginOptions);

/**
 * The sign-in prompt guests see when they try something that needs an
 * account (add to cart, checkout, pay, subscribe, buy AI Credits, open an
 * account-only page, or any API call that comes back 401).
 *
 * Opened by the `$e.user.needs.authentication` event - fire it with
 * requireAuth("reason") from helpers.js rather than directly. It's a
 * bottom offcanvas on phones and a centred modal on larger screens, and
 * shows the same Google / Apple / email options as the /login page
 * (LoginOptions), so the user never has to leave the page they're on.
 *
 * Closes itself once the user is signed in. If they were heading to an
 * account-only page, Auth.redirectIfNeeded() then takes them there.
 */
export default class LoginModal extends Component {
	holder = null;
	isOpen = false;
	reason = state(null);

	render(...args) {
		return h.empty();
	}

	$$user = {
		needs: {
			authentication: (ed) => this.show(ed),
			closeLoginPrompt: () => this.hide(),
		},

		authenticated: () => this.hide(),
	};

	show(ed) {
		if (authenticated()) return;

		// Already on the full login page - nothing to pop up.
		if (app("router").is("user.login")) return;

		let reason = null;
		try {
			reason = eParse(ed).message.get("reason", null);
		} catch (e) {
			reason = null;
		}

		this.reason.value = reason;

		if (this.isOpen) return;

		if (this.holder) cleanDispose(this.holder);

		if (isMobile()) {
			this.holder = tool.offcanvas(
				h.div(
					{
						class: "offcanvas-body p-4",
						style: "flex-grow: 1; overflow-y: auto;",
					},
					this.renderContent(),
				),
				null,
				"bottom",
				{ id: "login-prompt", style: "max-height: 92vh; height: auto;" },
			);
		} else {
			this.holder = tool.plainModal(
				h.div(
					{ class: "modal-content border-0 rounded-4 shadow-lg" },
					h.div({ class: "modal-body p-4 p-md-5" }, this.renderContent()),
				),
				{ id: "login-prompt", data_bs_backdrop: "true" },
			);
		}

		const elem = this.holder._element;

		elem?.addEventListener("hidden.bs.modal", () => this.onHidden());
		elem?.addEventListener("hidden.bs.offcanvas", () => this.onHidden());

		this.isOpen = true;
		this.holder.show();
	}

	hide() {
		if (this.holder && this.isOpen) {
			this.holder.hide();
		}
	}

	onHidden() {
		this.isOpen = false;

		// Closed without signing in: forget the account-only page they
		// were heading to, so a later sign-in doesn't jump there.
		if (!authenticated()) {
			Preferences.remove({ key: "redirect-url-after-auth" });
		}
	}

	renderContent() {
		return h.div(
			{ class: "w-100 mx-auto", style: "max-width: 420px;" },

			// Header
			h.div(
				{ class: "d-flex justify-content-between align-items-start mb-3" },
				h.div(
					h.h4({ class: "fw-bold mb-1", style: "color: #010101;" }, "Sign In to Continue"),
					v(this.reason, (reason) =>
						h.p(
							{ class: "text-muted mb-0 small" },
							reason.value ??
								"You'll need a Carata account for this.",
						),
					),
				),
				h.button({
					type: "button",
					class: "btn-close bg-light rounded-circle p-2 ms-3 flex-shrink-0",
					onclick: this.method("hide"),
					aria_label: "Close",
				}),
			),

			h.LoginOptions({ idPrefix: "login-prompt", inPrompt: true }),

			// Dismiss
			h.div(
				{ class: "text-center mt-3" },
				h.button(
					{
						type: "button",
						class: "btn btn-link text-muted small text-decoration-none fw-500",
						onclick: this.method("hide"),
					},
					"Not now, keep browsing",
				),
			),
		);
	}

	cleanUp() {
		super.cleanUp();
		if (this.holder) cleanDispose(this.holder);
	}
}

ojs(LoginModal);
